import { queryClient, request, useCurrentUserId, toastError } from '@/utils';
import { InfiniteData, useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { NotificationListRes, UnreadCountRes } from './type';
import produce from 'immer';
import { transFormRes } from './utils';
import { useCurrentAddressInfo, useUpdateCurrentAddressInfo } from '@/hooks';
import { message } from 'antd';

const UNREAD_NOTI_COUNT_INTERVAL = 10 * 1000;

const UNREAD_COUNT_URL = '/notification/unread_count';
const NOTI_LIST_URL = '/notification/list';

export const useUnreadNotificationCount = () => {
  const currentAddress = useCurrentUserId();
  const { isVerified } = useCurrentAddressInfo();

  const url = UNREAD_COUNT_URL;
  return useQuery([url, currentAddress], () => request.get<UnreadCountRes>(url), {
    select: res => res.unread_count,
    refetchInterval: UNREAD_NOTI_COUNT_INTERVAL,
    refetchOnWindowFocus: true,
    enabled: !!isVerified,
  });
};

const LIST_LIMIT = 20;

export type NotificationType = 'follow' | 'mentioned' | 'other' | '';

export const useNotificationList = (type: NotificationType) => {
  const currentAddress = useCurrentUserId();
  const url = NOTI_LIST_URL;
  return useInfiniteQuery(
    [url, currentAddress, type],
    ({ pageParam = 0 }) =>
      request.get<NotificationListRes>(url, {
        limit: LIST_LIMIT,
        start: pageParam,
        type,
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage?.notifications?.length < LIST_LIMIT) {
          return false;
        }
        return allPages.length * LIST_LIMIT;
      },
      select: data => {
        return produce(data, draft => {
          draft.pages.forEach(page => {
            page.notifications.forEach(i => transFormRes(i));
          });
        });
      },
    },
  );
};

export const useReadAllNotification = () => {
  const url = '/notification/read_all';
  const currentAddress = useCurrentUserId();
  return useMutation(() => request.post<{ is_success: boolean }>(url), {
    onSuccess: res => {
      if (res.is_success) {
        // message.success('Marked');
        setReadStatus({ currentAddress: currentAddress! });
        queryClient.invalidateQueries([UNREAD_COUNT_URL, currentAddress]);
      }
    },
    onError(err: string) {
      // toastError(err);
    },
  });
};

export const useReadNotification = (id: string) => {
  const url = '/notification/read';
  const currentAddress = useCurrentUserId();

  return useMutation(() => request.post<{ is_success: boolean }>(url, { id }), {
    onSuccess: res => {
      if (res.is_success) {
        setReadStatus({ currentAddress: currentAddress!, id });
        queryClient.invalidateQueries([UNREAD_COUNT_URL, currentAddress]);
      }
    },
    onError(err: string) {
      toastError(err);
    },
  });
};

const setReadStatus = ({ currentAddress, id }: { currentAddress: string; id?: string }) => {
  const queryKey = [NOTI_LIST_URL, currentAddress];
  if (queryClient.getQueriesData(queryKey)) {
    queryClient.setQueriesData<InfiniteData<NotificationListRes> | undefined>(queryKey, pre => {
      if (!pre) {
        return pre;
      }
      return produce(pre, draft => {
        draft.pages.forEach(page => {
          if (id) {
            const record = page.notifications.find(i => i.id === id);
            if (record) {
              record.is_read = true;
            }
          } else {
            page.notifications.forEach(i => {
              i.is_read = true;
            });
          }
        });
      });
    });
  }
};

export const useEnableNotification = () => {
  const url = '/user/update_enable_notification';
  const currentAddress = useCurrentUserId();
  const update = useUpdateCurrentAddressInfo();
  return useMutation((enable: boolean) => request.post<{ is_success: boolean }>(url, { enable }), {
    onSuccess: res => {
      if (res.is_success) {
        update(pre => {
          return produce(pre, draft => {
            if (draft?.userInfo) {
              draft.userInfo.enable_notification = !draft.userInfo.enable_notification;
            }
          });
        });
        setReadStatus({ currentAddress: currentAddress! });
      } else {
        message.error('Failed');
      }
    },
    onError(err: string) {
      toastError(err);
    },
  });
};
